// Step 2 of self-serve account deletion — the irreversible one. Mirrors
// thea-serverless/functions/theaWebMutations/theaWebConfirmAccountDeletion.py.

import type { AccountDeletionMode } from './theaWebRequestAccountDeletion';

/**
 * Exactly ONE proof, never both — a request carrying both is rejected as a
 * client that does not know which flow it is in.
 *
 *  - `code`: the 6-digit code mailed to a permanent account. This is the
 *    re-authentication factor; the server enforces its 15-minute freshness.
 *  - `confirmPhrase`: the literal "DELETE", typed by an anonymous user who has
 *    no mailbox to receive a code at, or by an account whose address was never
 *    verified. An intent control, not a security one — on the reauth path the
 *    security control is the freshly re-authenticated session the server reads
 *    off the ID token's `auth_time`, which is why the password is never sent
 *    here and there is no field for it.
 */
export interface TheaWebConfirmAccountDeletionRequest {
  code?: string;
  confirmPhrase?: string;
}

export interface TheaWebConfirmAccountDeletionResponse {
  /**
   * Always true on a 2xx. By the time this returns the Auth user is disabled or
   * gone, so from the user's side the account IS deleted — `complete` reports
   * whether the server-side purge also finished, which is our operational
   * concern, not something the user can act on.
   */
  deleted: true;
  mode: AccountDeletionMode;
  /** Tombstone id in the `_audit` trail — the user's deletion receipt. */
  auditId: string;
  /** False when work was left for the resumable job (e.g. a huge account). */
  complete: boolean;
  /** Matrix keys actually purged. Ids/enums only, never content. */
  collectionsPurged: string[];
}

/**
 * Server error messages this flow maps to UI states. The BE returns these as
 * bare, stable strings (never sentences) precisely so the FE can branch without
 * matching on prose.
 */
export const ACCOUNT_DELETION_ERRORS = {
  /**
   * A VERIFIED account tried the typed-phrase path. Send them to the code.
   * Unchanged by THE-487 — this is still the "go read your inbox" answer.
   */
  REAUTH_REQUIRED: 'REAUTH_REQUIRED',
  /**
   * An UNVERIFIED account typed the phrase without a recent re-authentication.
   * A different instruction from REAUTH_REQUIRED — "re-enter your password",
   * not "check your email" — and therefore a different token: for the typo'd
   * user this branch exists to serve, the inbox is precisely what does not
   * work.
   */
  RECENT_LOGIN_REQUIRED: 'RECENT_LOGIN_REQUIRED',
  /** Anonymous caller sent a code. There is no code for an anon session. */
  NO_CODE_FOR_ANON: 'NO_CODE_FOR_ANON',
  /** A permanent account whose provider gave us no address. Needs support. */
  NO_EMAIL_ON_ACCOUNT: 'NO_EMAIL_ON_ACCOUNT',
  /** Per-uid request throttle. */
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
} as const;
